
import React, { useState } from "react";
import { Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Edit, Trash2 } from "lucide-react";
import { Post } from "@/hooks/posts/types";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { LazyVideo } from "../LazyVideo";

interface VideoPostGridProps {
  posts: Post[];
  onPostClick: (post: Post) => void;
  onEdit?: (post: Post) => void;
  onDelete?: (postId: string) => void;
  showActions?: boolean;
}

export const VideoPostGrid = ({ 
  posts, 
  onPostClick, 
  onEdit, 
  onDelete, 
  showActions = false 
}: VideoPostGridProps) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletePostId, setDeletePostId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const handleEdit = (post: Post, e: React.MouseEvent) => {
    e.stopPropagation();
    if (onEdit) {
      onEdit(post);
    }
  };

  const confirmDelete = async () => {
    if (deletePostId && onDelete) {
      setIsDeleting(true);
      try {
        await onDelete(deletePostId);
      } finally {
        setIsDeleting(false);
        setDeletePostId(null);
      }
    }
  };

  const handleDelete = (postId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeletePostId(postId);
    setDeleteDialogOpen(true);
  };

  console.log('VideoPostGrid rendering:', { 
    postsCount: posts.length, 
    samplePost: posts[0],
    sampleVideoUrl: posts[0]?.image || 'No video URL'
  });

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      {posts.map((post) => (
        <div
          key={post.id}
          className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden cursor-pointer group hover:shadow-md transition-shadow"
          onClick={() => onPostClick(post)}
        >
          {/* Lazy Video Thumbnail */}
          <LazyVideo
            src={post.image}
            className="w-full h-full object-cover"
            alt={post.content || "Video post"}
            width={300}
            height={300}
            controls={false}
            postId={post.id}
            onError={() => {
              console.log('Video failed to load:', post.image);
            }}
          />
          
          {/* Play Button Overlay - no hover transitions to prevent video churn */}
          <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center">
            <div className="bg-white bg-opacity-90 rounded-full p-3">
              <Play className="text-gray-800 w-6 h-6 fill-current" />
            </div>
          </div>
          
          {/* Action Buttons */}
          {showActions && (
            <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">{/* Keep action button transitions */}
              <Button
                size="sm"
                variant="outline"
                onClick={(e) => handleEdit(post, e)}
                className="h-7 w-7 p-0 bg-white hover:bg-gray-100"
              >
                <Edit size={12} />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={(e) => handleDelete(post.id, e)}
                className="h-7 w-7 p-0 bg-white hover:bg-red-50 text-red-600 hover:text-red-700"
              >
                <Trash2 size={12} />
              </Button>
            </div>
          )}
          
          {/* Content Text Overlay */}
          <div className="absolute bottom-2 left-2 right-2">
            <p className="text-white text-xs truncate bg-black bg-opacity-50 px-2 py-1 rounded">
              {post.content.substring(0, 50)}...
            </p>
          </div>
        </div>
      ))}

      <ConfirmationDialog
        isOpen={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Post"
        description="Are you sure you want to delete this post? This action cannot be undone and the post cannot be restored."
        confirmText="Delete"
        variant="destructive"
        onConfirm={confirmDelete}
        loading={isDeleting}
      />
    </div>
  );
};
